import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { RefreshCw, AlertTriangle, Clock, CheckCircle, XCircle, Zap, Bell, User, CreditCard, ArrowRight, X, Search, Shield } from 'lucide-react';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatAmount = (n, currency = 'XOF') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(n);
};

const timeAgo = (date) => {
  if (!date) return '—';
  const hours = Math.round((Date.now() - new Date(date)?.getTime()) / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}j ${hours % 24}h`;
  return `${hours}h`;
};

const ESCALATION_THRESHOLD_HOURS = 24;
const CRITICAL_THRESHOLD_HOURS = 72;

const CHANNEL_LABELS = {
  bank_transfer: 'Virement Bancaire',
  mobile_money: 'Mobile Money',
  paystack: 'Paystack',
  orange_money: 'Orange Money',
  mtn_momo: 'MTN MoMo',
  wave: 'Wave'
};

const RETRY_STATUS = {
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-700 border-red-200' },
  retrying: { label: 'Nouvelle tentative', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  escalated: { label: 'Escaladé', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  manual_override: { label: 'Override Manuel', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  resolved: { label: 'Résolu', color: 'bg-green-100 text-green-700 border-green-200' }
};

const EscalationTimer = ({ failedAt, retryCount }) => {
  const hours = Math.round((Date.now() - new Date(failedAt)?.getTime()) / (1000 * 60 * 60));
  const isCritical = hours >= CRITICAL_THRESHOLD_HOURS;
  const isEscalated = hours >= ESCALATION_THRESHOLD_HOURS;
  const color = isCritical ? 'text-red-600' : isEscalated ? 'text-orange-600' : 'text-amber-600';
  const bg = isCritical ? 'bg-red-50 border-red-200' : isEscalated ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200';
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${bg} ${color}`}>
      <Clock size={11} className={isCritical ? 'animate-pulse' : ''} />
      {timeAgo(failedAt)} · {retryCount || 0} tentative{retryCount !== 1 ? 's' : ''}
      {isCritical && <AlertTriangle size={11} className="animate-pulse" />}
    </div>
  );
};

const OverrideModal = ({ transfer, onClose, onConfirm, loading }) => {
  const [action, setAction] = useState('retry');
  const [note, setNote] = useState('');
  const [newChannel, setNewChannel] = useState('');

  if (!transfer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Shield size={18} className="text-orange-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Override Manuel — Finance</h3>
              <p className="text-sm text-gray-500">{formatAmount(transfer?.net_payout_amount, transfer?.payout_currency)}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Transfer details */}
          <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Bénéficiaire</p>
              <p className="font-semibold text-gray-900 mt-0.5">{transfer?.recipient_name || transfer?.recipient_id?.slice(0, 8)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Canal actuel</p>
              <p className="font-semibold text-gray-900 mt-0.5">{CHANNEL_LABELS?.[transfer?.payout_channel] || transfer?.payout_channel || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tentatives</p>
              <p className="font-semibold text-gray-900 mt-0.5">{transfer?.retry_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Dernier échec</p>
              <p className="font-semibold text-gray-900 mt-0.5">{formatDate(transfer?.last_failed_at || transfer?.updated_at)}</p>
            </div>
          </div>

          {/* Action selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Action</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { val: 'retry', label: 'Relancer', icon: RefreshCw, color: 'blue' },
                { val: 'change_channel', label: 'Changer canal', icon: ArrowRight, color: 'amber' },
                { val: 'mark_resolved', label: 'Marquer résolu', icon: CheckCircle, color: 'green' }
              ]?.map(({ val, label, icon: IconComponent, color }) => (
                <button
                  key={val}
                  onClick={() => setAction(val)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 text-xs font-semibold transition-all ${action === val ? `border-${color}-500 bg-${color}-50 text-${color}-700` : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                >
                  <IconComponent size={16} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {action === 'change_channel' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nouveau canal</label>
              <select
                value={newChannel}
                onChange={e => setNewChannel(e?.target?.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Sélectionner un canal...</option>
                {Object.entries(CHANNEL_LABELS)?.map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Note Finance (obligatoire)</label>
            <textarea
              value={note}
              onChange={e => setNote(e?.target?.value)}
              rows={3}
              placeholder="Raison de l'intervention manuelle, détails de la correction..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        <div className="p-5 border-t border-gray-200 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button
            onClick={() => onConfirm(transfer?.id, action, note, newChannel)}
            disabled={!note?.trim() || (action === 'change_channel' && !newChannel) || loading}
            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Shield size={15} />}
            Confirmer Override
          </button>
        </div>
      </div>
    </div>
  );
};

const FailedTransferRetryQueue = () => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [notifying, setNotifying] = useState(null);
  const [stats, setStats] = useState({ total: 0, critical: 0, escalated: 0, totalAmount: 0 });

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase?.from('commission_payouts')?.select('*, user_profiles!commission_payouts_recipient_id_fkey(full_name, email, phone)')?.in('routing_status', ['failed', 'retrying', 'escalated', 'manual_override'])?.order('updated_at', { ascending: false })?.limit(100);

      const rows = (data || [])?.map(r => ({
        ...r,
        recipient_name: r?.user_profiles?.full_name,
        recipient_email: r?.user_profiles?.email,
        recipient_phone: r?.user_profiles?.phone
      }));

      const now = Date.now();
      const critical = rows?.filter(r => {
        const hrs = (now - new Date(r?.updated_at)?.getTime()) / (1000 * 60 * 60);
        return hrs >= CRITICAL_THRESHOLD_HOURS;
      });

      const escalated = rows?.filter(r => {
        const hrs = (now - new Date(r?.updated_at)?.getTime()) / (1000 * 60 * 60);
        return hrs >= ESCALATION_THRESHOLD_HOURS;
      });

      setTransfers(rows);
      setStats({
        total: rows?.length,
        critical: critical?.length,
        escalated: escalated?.length,
        totalAmount: rows?.reduce((s, r) => s + (r?.net_payout_amount || 0), 0)
      });
    } catch (err) {
      console.error('Error loading failed transfers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  // Auto-escalate transfers past threshold
  useEffect(() => {
    const autoEscalate = async () => {
      const now = Date.now();
      const toEscalate = transfers?.filter(t => {
        const hrs = (now - new Date(t?.updated_at)?.getTime()) / (1000 * 60 * 60);
        return hrs >= ESCALATION_THRESHOLD_HOURS && t?.routing_status === 'failed';
      });
      if (toEscalate?.length > 0) {
        await Promise.all(toEscalate?.map(t =>
          supabase?.from('commission_payouts')?.update({ routing_status: 'escalated' })?.eq('id', t?.id)
        ));
      }
    };
    if (transfers?.length > 0) autoEscalate();
  }, [transfers]);

  const handleRetry = async (id) => {
    setActionLoading(true);
    try {
      const t = transfers?.find(x => x?.id === id);
      await supabase?.from('commission_payouts')?.update({
          routing_status: 'retrying',
          retry_count: (t?.retry_count || 0) + 1,
          last_retry_at: new Date()?.toISOString()
        })?.eq('id', id);
      await loadTransfers();
    } catch (err) {
      console.error('Error retrying transfer:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleNotify = async (transfer) => {
    setNotifying(transfer?.id);
    try {
      // Insert notification record
      await supabase?.from('notifications')?.insert({
        user_id: transfer?.recipient_id,
        type: 'payout_failed',
        title: 'Échec de virement',
        message: `Votre virement de ${formatAmount(transfer?.net_payout_amount, transfer?.payout_currency)} a échoué. Notre équipe finance travaille à résoudre ce problème.`,
        metadata: { transfer_id: transfer?.id, retry_count: transfer?.retry_count || 0 },
        is_read: false
      });
    } catch (err) {
      console.error('Error sending notification:', err);
    } finally {
      setNotifying(null);
    }
  };

  const handleOverride = async (id, action, note, newChannel) => {
    setActionLoading(true);
    try {
      const updates = {
        override_note: note,
        manual_override_at: new Date()?.toISOString()
      };
      if (action === 'retry') {
        updates.routing_status = 'retrying';
        updates.retry_count = (transfers?.find(t => t?.id === id)?.retry_count || 0) + 1;
      } else if (action === 'change_channel') {
        updates.routing_status = 'retrying';
        updates.payout_channel = newChannel;
      } else if (action === 'mark_resolved') {
        updates.routing_status = 'resolved';
      }
      updates.routing_status = action === 'mark_resolved' ? 'completed' : 'manual_override';

      await supabase?.from('commission_payouts')?.update(updates)?.eq('id', id);
      setSelectedTransfer(null);
      await loadTransfers();
    } catch (err) {
      console.error('Error applying override:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = transfers?.filter(t => {
    const matchStatus = filterStatus === 'all' || t?.routing_status === filterStatus;
    const matchSearch = !searchQuery ||
      t?.recipient_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      t?.id?.includes(searchQuery);
    return matchStatus && matchSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">File de Réessai — Virements Échoués</h2>
          <p className="text-sm text-gray-500 mt-0.5">Virements bancaires et mobile money en échec avec escalade automatique</p>
        </div>
        <button
          onClick={loadTransfers}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
        >
          <RefreshCw size={15} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total en échec', value: stats?.total, color: 'bg-red-100 text-red-700', icon: XCircle },
          { label: 'Critiques (>72h)', value: stats?.critical, color: 'bg-red-600 text-white', icon: AlertTriangle },
          { label: 'Escaladés (>24h)', value: stats?.escalated, color: 'bg-purple-100 text-purple-700', icon: Zap },
          { label: 'Montant total bloqué', value: formatAmount(stats?.totalAmount), color: 'bg-amber-100 text-amber-700', icon: CreditCard }
        ]?.map(({ label, value, color, icon: IconComponent }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
              <IconComponent size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-lg font-bold text-gray-900 truncate">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Escalation Alert Banner */}
      {stats?.critical > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 animate-pulse" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800">{stats?.critical} virement{stats?.critical > 1 ? 's' : ''} critique{stats?.critical > 1 ? 's' : ''} — intervention immédiate requise</p>
            <p className="text-xs text-red-600 mt-0.5">Ces virements sont bloqués depuis plus de 72h et nécessitent un override manuel de l'équipe finance.</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 shadow-sm">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par bénéficiaire ou ID..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e?.target?.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Tous les statuts</option>
          <option value="failed">Échoués</option>
          <option value="retrying">En cours de réessai</option>
          <option value="escalated">Escaladés</option>
          <option value="manual_override">Override Manuel</option>
        </select>
      </div>

      {/* Transfer Queue Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filtered?.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle size={36} className="text-green-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">Aucun virement en échec</p>
            <p className="text-xs text-gray-400 mt-1">Tous les virements ont été traités avec succès</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Bénéficiaire</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Canal</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ancienneté / Tentatives</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered?.map(t => {
                  const st = RETRY_STATUS?.[t?.routing_status] || RETRY_STATUS?.failed;
                  const hrs = Math.round((Date.now() - new Date(t?.updated_at)?.getTime()) / (1000 * 60 * 60));
                  const isCritical = hrs >= CRITICAL_THRESHOLD_HOURS;
                  return (
                    <tr key={t?.id} className={`hover:bg-gray-50 transition-colors ${isCritical ? 'bg-red-50/30' : ''}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCritical ? 'bg-red-100' : 'bg-gray-100'}`}>
                            <User size={14} className={isCritical ? 'text-red-600' : 'text-gray-500'} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{t?.recipient_name || t?.recipient_id?.slice(0, 8)}</p>
                            {t?.recipient_email && <p className="text-xs text-gray-400">{t?.recipient_email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">
                          <CreditCard size={11} />
                          {CHANNEL_LABELS?.[t?.payout_channel] || t?.payout_channel || '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right font-bold text-gray-900">
                        {formatAmount(t?.net_payout_amount, t?.payout_currency)}
                      </td>
                      <td className="px-5 py-3.5">
                        <EscalationTimer failedAt={t?.updated_at} retryCount={t?.retry_count} />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${st?.color}`}>
                          {st?.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleRetry(t?.id)}
                            disabled={actionLoading || t?.routing_status === 'retrying'}
                            title="Relancer"
                            className="p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-40 border border-blue-200"
                          >
                            <RefreshCw size={13} />
                          </button>
                          <button
                            onClick={() => handleNotify(t)}
                            disabled={notifying === t?.id}
                            title="Notifier l'utilisateur"
                            className="p-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-40 border border-amber-200"
                          >
                            {notifying === t?.id ? <div className="w-3 h-3 border border-amber-700 border-t-transparent rounded-full animate-spin" /> : <Bell size={13} />}
                          </button>
                          <button
                            onClick={() => setSelectedTransfer(t)}
                            title="Override Manuel Finance"
                            className="p-1.5 rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors border border-orange-200"
                          >
                            <Shield size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Override Modal */}
      {selectedTransfer && (
        <OverrideModal
          transfer={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
          onConfirm={handleOverride}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default FailedTransferRetryQueue;
